import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { navigation } from '../../data/mock';
import { Menu, X, ChevronDown } from 'lucide-react';

const NavLink = ({ to, children, isActive }) => (
  <Link
    to={to}
    className={`nav-link-underline relative py-1 transition-colors duration-300 ${
      isActive ? 'text-[#3eb8b3] active' : 'text-white/70 hover:text-white'
    }`}
  >
    {children}
  </Link>
);

const Dropdown = ({ label, isActive, children }) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        className={`flex items-center gap-1 py-1 transition-colors duration-300 ${
          isActive ? 'text-[#3eb8b3]' : 'text-white/70 hover:text-white'
        }`}
      >
        {label}
        <ChevronDown
          size={12}
          className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-300 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-2'
        }`}
      >
        <div className="bg-[#0d1b2a]/95 backdrop-blur-md border border-white/10 rounded px-1 py-1 min-w-[160px]">
          {children}
        </div>
      </div>
    </div>
  );
};

const DropdownLink = ({ to, children, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="block px-4 py-2.5 text-white/60 hover:text-[#3eb8b3] hover:bg-white/5 transition-colors rounded text-xs tracking-wider"
  >
    {children}
  </Link>
);

const Header = () => {
  const { language, setLanguage } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const nav = navigation[language];

  const isHome = location.pathname === '/' || location.pathname === '/home';

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const isPath = (path) => location.pathname === path;
  const startsWithPath = (path) => location.pathname.startsWith(path);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isHome ? 'bg-transparent' : 'bg-[#1a2a3a]/95 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="shrink-0">
          <img src="/logo.svg" alt="Okuant" className="h-7 sm:h-9" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7 text-sm tracking-wider">
          <NavLink to="/" isActive={isPath('/') || isPath('/home')}>
            {nav.home}
          </NavLink>

          <NavLink to="/corporativo/tecnologia" isActive={isPath('/corporativo/tecnologia')}>
            {nav.technologyIMT}
          </NavLink>

          <Dropdown
            label={nav.services}
            isActive={startsWithPath('/servicios')}
          >
            <DropdownLink to="/servicios/inversion">{nav.investment}</DropdownLink>
            <DropdownLink to="/servicios/gestion">{nav.management}</DropdownLink>
          </Dropdown>

          <Dropdown
            label={nav.corporate}
            isActive={startsWithPath('/corporativo') && !isPath('/corporativo/tecnologia')}
          >
            <DropdownLink to="/corporativo/compania">{nav.company}</DropdownLink>
            <DropdownLink to="/corporativo/tecnologia">{nav.technology}</DropdownLink>
          </Dropdown>

          <NavLink to="/contacto" isActive={isPath('/contacto')}>
            {nav.contact}
          </NavLink>
        </nav>

        {/* Right side controls */}
        <div className="flex items-center gap-3 sm:gap-6">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => setLanguage('es')}
              className={`transition-colors ${language === 'es' ? 'text-[#3eb8b3]' : 'text-white/70 hover:text-white'}`}
            >
              ES
            </button>
            <span className="text-white/30">|</span>
            <button
              onClick={() => setLanguage('en')}
              className={`transition-colors ${language === 'en' ? 'text-[#3eb8b3]' : 'text-white/70 hover:text-white'}`}
            >
              EN
            </button>
          </div>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden text-white p-2 hover:text-[#3eb8b3] transition-colors"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <div
        className={`lg:hidden fixed inset-0 top-[64px] sm:top-[72px] bg-[#1a2a3a]/98 backdrop-blur-md transition-all duration-300 ${menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
      >
        <nav className="flex flex-col items-center justify-center h-full gap-5 sm:gap-6 px-6 overflow-y-auto py-8">
          <Link to="/" onClick={() => setMenuOpen(false)}
            className="text-white text-2xl font-light hover:text-[#3eb8b3] transition-colors">
            {nav.home}
          </Link>

          <Link to="/corporativo/tecnologia" onClick={() => setMenuOpen(false)}
            className="text-white text-2xl font-light hover:text-[#3eb8b3] transition-colors">
            {nav.technologyIMT}
          </Link>

          <span className="text-white/40 text-xs tracking-[0.3em] uppercase mt-2">{nav.services}</span>
          <Link to="/servicios/inversion" onClick={() => setMenuOpen(false)}
            className="text-white/70 text-xl font-light hover:text-[#3eb8b3] transition-colors">
            {nav.investment}
          </Link>
          <Link to="/servicios/gestion" onClick={() => setMenuOpen(false)}
            className="text-white/70 text-xl font-light hover:text-[#3eb8b3] transition-colors">
            {nav.management}
          </Link>

          <span className="text-white/40 text-xs tracking-[0.3em] uppercase mt-2">{nav.corporate}</span>
          <Link to="/corporativo/compania" onClick={() => setMenuOpen(false)}
            className="text-white/70 text-xl font-light hover:text-[#3eb8b3] transition-colors">
            {nav.company}
          </Link>
          <Link to="/corporativo/tecnologia" onClick={() => setMenuOpen(false)}
            className="text-white/70 text-xl font-light hover:text-[#3eb8b3] transition-colors">
            {nav.technology}
          </Link>
          <Link to="/contacto" onClick={() => setMenuOpen(false)}
            className="text-white text-2xl font-light hover:text-[#3eb8b3] transition-colors mt-2">
            {nav.contact}
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
